import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { Buffer } from 'buffer';
import React, { Component } from 'react';
import { fg } from '@atlaskit/platform-feature-flags';
import { isApiGatewayUrl } from '../utils/is-api-gateway-url';
import truncate from '../utils/Truncate';
import FeedbackForm from './FeedbackForm';
const MAX_SUMMARY_LENGTH_CHARS = 100;
const singleLineTruncatedText = (text, length = MAX_SUMMARY_LENGTH_CHARS) => {
  const singleLineText = text.replace(/\n/g, ' ');
  return truncate(singleLineText, length);
};
export default class FeedbackCollector extends Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      anonymousFeedback: true
    });
    _defineProperty(this, "postFeedback", async formValues => {
      const entrypointId = this.props.entrypointId;
      let fetchUrl = this.getFeedbackUrl();

      // Don't dispatch unless we have suitable props (allows tests to pass through empty strings and avoid redundant network calls)
      if (entrypointId) {
        const formData = await this.mapFormToJSD(formValues);
        const body = {
          feedback: {
            entrypointId: this.props.entrypointId,
            entrypointUiVersion: this.getPackageVersion(),
            ...formData
          }
        };
        if (isApiGatewayUrl(fetchUrl)) {
          fetchUrl += '/feedback-collector-api';
        } else if (!this.props.customFeedbackUrl) {
          fetchUrl = 'https://feedback-collector-api.services.atlassian.com';
        }
        const postData = Buffer.from(JSON.stringify(body)).toString('base64');
        fetch(`${fetchUrl}/v2/feedback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            data: postData
          }),
          credentials: 'include'
        });
      }
      this.props.onClose();

      // slightly delay confirming submit since we don't wait for the REST call to succeed
      //
      // Because `onClose` is invoked prior to this timeout triggering, the `componentWillUnmount`
      // may occur before the `onSubmit` is called. To prevent prematurely cancelling the
      // network request, we deliberately don't clear this timeout inside `componentWillUnmount`.
      //
      setTimeout(() => this.props.onSubmit(formValues), this.props.timeoutOnSubmit);
    });
  }
  async componentDidMount() {
    const anonymousFeedback = !(await this.shouldShowOptInCheckboxesNew());
    this.setState({
      anonymousFeedback
    });
  }
  getGatewayUrl() {
    const {
      customGatewayUrl,
      url
    } = this.props;
    if (customGatewayUrl) {
      return customGatewayUrl;
    }
    if (url) {
      return url;
    }
    return FeedbackCollector.defaultProps.url;
  }
  getFeedbackUrl() {
    const {
      customFeedbackUrl,
      url
    } = this.props;
    if (customFeedbackUrl) {
      return customFeedbackUrl;
    }
    if (url) {
      return url;
    }
    return FeedbackCollector.defaultProps.url;
  }
  getPackageVersion() {
    return "13.14.0" || 'Unknown, at least 11.0.0';
  }
  async getEntitlementInformation() {
    var _entitlementDetails, _entitlementDetails2, _productName, _entitlement, _productEntitlement;
    const url = this.getGatewayUrl();
    // jira / confluence / bitbucket / trello
    let productName;
    let productEntitlement;
    let entitlementDetails;
    let productKey;
    if (window.location.host.includes('bitbucket.org')) {
      var _document$querySelect;
      productName = 'Bitbucket';
      productKey = 'bitbucket';
      entitlementDetails = JSON.parse(JSON.stringify((_document$querySelect = document.querySelector('meta[id="bb-bootstrap"]')) === null || _document$querySelect === void 0 ? void 0 : _document$querySelect.getAttribute('data-current-user')));
      const hasPremium = entitlementDetails['hasPremium'];
      productEntitlement = hasPremium ? 'PREMIUM' : 'STANDARD';
    } else if (['trellis.coffee', 'trello.com'].includes(window.location.host)) {
      productName = 'Trello';
      productKey = 'trello';
    } else {
      if (document.querySelector('meta[id="confluence-context-path"]')) {
        productName = 'Confluence';
        productKey = 'pricingplan.confluence.ondemand';
      } else {
        productName = 'Jira';
        productKey = 'jira-software.ondemand';
      }
      try {
        entitlementDetails = await fetch(`${url}/customer-context/entitlements/${window.location.host}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          },
          ...(isApiGatewayUrl(url) ? {
            credentials: 'include'
          } : {})
        });
      } catch (e) {
        entitlementDetails = undefined;
      }
    }
    let entitlement;
    const entitlementInformation = [];
    const cloudSiteId = ((_entitlementDetails = entitlementDetails) === null || _entitlementDetails === void 0 ? void 0 : _entitlementDetails.cloudSiteId) || '';
    if ((_entitlementDetails2 = entitlementDetails) !== null && _entitlementDetails2 !== void 0 && _entitlementDetails2.children) {
      var _entitlementDetails3;
      entitlement = (_entitlementDetails3 = entitlementDetails) === null || _entitlementDetails3 === void 0 ? void 0 : _entitlementDetails3.children.find(entitlement => {
        return entitlement.key === productKey;
      });
    }
    entitlementInformation.push({
      id: 'product',
      value: productName ? (_productName = productName) === null || _productName === void 0 ? void 0 : _productName.toLowerCase() : ''
    }, {
      id: 'hostingType',
      value: (_entitlement = entitlement) !== null && _entitlement !== void 0 && _entitlement.product ? entitlement.product.hostingType : 'CLOUD'
    }, {
      id: 'entitlementEdition',
      value: (_productEntitlement = productEntitlement) !== null && _productEntitlement !== void 0 ? _productEntitlement : ''
    }, {
      id: 'cloudId',
      value: cloudSiteId
    });
    return entitlementInformation;
  }
  getTypeFieldValue(dtype) {
    switch (dtype) {
      case 'bug':
        return this.props.typeBugDefaultValue;
      case 'comment':
        return this.props.typeCommentDefaultValue;
      case 'suggestion':
        return this.props.typeSuggestionDefaultValue;
      case 'question':
        return this.props.typeQuestionDefaultValue;
      case 'empty':
      default:
        return this.props.typeEmptyDefaultValue;
    }
  }
  async getAtlassianID() {
    const {
      atlassianAccountId,
      shouldGetEntitlementDetails
    } = this.props;
    try {
      if (atlassianAccountId || !shouldGetEntitlementDetails) {
        return atlassianAccountId;
      }
      const url = this.getGatewayUrl();
      const result = await fetch(`${url}/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        ...(isApiGatewayUrl(url) ? {
          credentials: 'include'
        } : {})
      });
      const json = await result.json();
      return json.account_id;
    } catch (e) {
      return undefined;
    }
  }
  shouldShowOptInCheckboxes() {
    if (this.props.anonymousFeedback) {
      return !this.props.anonymousFeedback;
    }
    if (this.props.atlassianAccountId) {
      return true;
    }
    let response;
    this.getAtlassianID().then(result => {
      response = result !== undefined;
    }).catch(() => {
      response = false;
    });
    // @ts-ignore
    return response;
  }
  shouldShowOptInCheckboxesNew() {
    return new Promise(resolve => {
      if (this.props.anonymousFeedback) {
        resolve(!this.props.anonymousFeedback);
      }
      if (this.props.atlassianAccountId) {
        resolve(true);
      }
      this.getAtlassianID().then(result => {
        resolve(result !== undefined);
      }).catch(() => {
        resolve(false);
      });
    });
  }
  getDescription(formValues) {
    return formValues.description || this.props.descriptionDefaultValue;
  }
  getSummary(formValues) {
    return singleLineTruncatedText(formValues.description, this.props.summaryTruncateLength) || this.props.summaryDefaultValue;
  }
  getCustomerName() {
    var _this$props$name;
    return (_this$props$name = this.props.name) !== null && _this$props$name !== void 0 ? _this$props$name : this.props.customerNameDefaultValue;
  }
  addEmailToContext() {
    const contextField = this.props.additionalFields.find(field => {
      return field.id === 'customfield_10047';
    });
    if (contextField) {
      contextField.value = `${contextField.value}
        email: ${this.props.email}`;
    } else {
      this.props.additionalFields.push({
        id: 'customfield_10047',
        value: `email: ${this.props.email}`
      });
    }
  }
  addAccountIdToContext(atlassianID) {
    if (atlassianID) {
      const contextField = this.props.additionalFields.find(field => {
        return field.id === 'customfield_10047';
      });
      if (contextField) {
        contextField.value = `${contextField.value}
			atlassian-account-id: ${atlassianID}`;
      }
    }
  }
  async mapFormToJSD(formValues) {
    var _this$props, _this$props2;
    let entitlementInformation = null;
    if ((_this$props = this.props) !== null && _this$props !== void 0 && _this$props.shouldGetEntitlementDetails) {
      entitlementInformation = await this.getEntitlementInformation();
    }
    if ((_this$props2 = this.props) !== null && _this$props2 !== void 0 && _this$props2.email) {
      this.addEmailToContext();
    }
    const atlassianID = await this.getAtlassianID();
    if (fg('jsw_feedback_account_id')) {
      this.addAccountIdToContext(atlassianID);
    }
    return {
      fields: [...(entitlementInformation !== null && entitlementInformation || []), this.props.showTypeField ? {
        id: this.props.typeFieldId,
        value: this.getTypeFieldValue(formValues.type)
      } : undefined, {
        id: this.props.summaryFieldId,
        value: this.getSummary(formValues)
      }, {
        id: this.props.descriptionFieldId,
        value: this.getDescription(formValues)
      }, {
        id: 'aaidOrHash',
        value: formValues.canBeContacted ? atlassianID : undefined
      }, {
        id: this.props.customerNameFieldId,
        value: this.getCustomerName()
      }, formValues.canBeContacted ? {
        id: this.props.canBeContactedFieldId,
        value: this.props.canBeContactedAgreeValue
      } : {
        id: this.props.canBeContactedFieldId,
        value: this.props.canBeContactedDeclineValue
      }, formValues.enrollInResearchGroup ? {
        id: this.props.enrollInResearchFieldId,
        value: this.props.enrollInResearchAgreeValue
      } : {
        id: this.props.enrollInResearchFieldId,
        value: this.props.enrollInResearchDeclineValue
      }, ...this.props.additionalFields].filter(Boolean)
    };
  }
  render() {
    const anonymousFeedback = !this.shouldShowOptInCheckboxes();
    return /*#__PURE__*/React.createElement(FeedbackForm, {
      customContent: this.props.customContent,
      feedbackTitle: this.props.feedbackTitle,
      feedbackTitleDetails: this.props.feedbackTitleDetails,
      showTypeField: this.props.showTypeField,
      showDefaultTextFields: this.props.showDefaultTextFields,
      hasDescriptionDefaultValue: !!this.props.descriptionDefaultValue,
      canBeContactedLabel: this.props.canBeContactedLabel,
      enrolInResearchLabel: this.props.enrolInResearchLabel,
      summaryPlaceholder: this.props.summaryPlaceholder,
      submitButtonLabel: this.props.submitButtonLabel,
      cancelButtonLabel: this.props.cancelButtonLabel,
      feedbackGroupLabels: this.props.feedbackGroupLabels,
      onSubmit: this.postFeedback,
      onClose: this.props.onClose,
      locale: this.props.locale,
      anonymousFeedback: fg('platform.proforma-form-builder-feedback_hupaz') ? this.state.anonymousFeedback : anonymousFeedback,
      selectLabel: this.props.selectLabel,
      customTextAreaLabel: this.props.customTextAreaLabel,
      customFeedbackOptions: this.props.customFeedbackOptions,
      shouldReturnFocusRef: this.props.shouldReturnFocusRef
    });
  }
}
_defineProperty(FeedbackCollector, "defaultProps", {
  locale: 'en',
  url: '/gateway/api',
  shouldGetEntitlementDetails: true,
  canBeContactedFieldId: 'customfield_10043',
  canBeContactedAgreeValue: [{
    id: '10109'
  }],
  canBeContactedDeclineValue: [{
    id: '10111'
  }],
  additionalFields: [],
  customerNameFieldId: 'customfield_10045',
  customerNameDefaultValue: 'unknown',
  descriptionFieldId: 'description',
  descriptionDefaultValue: '',
  enrollInResearchFieldId: 'customfield_10044',
  enrollInResearchAgreeValue: [{
    id: '10110'
  }],
  enrollInResearchDeclineValue: [{
    id: '10112'
  }],
  summaryFieldId: 'summary',
  summaryDefaultValue: '',
  summaryTruncateLength: 100,
  timeoutOnSubmit: 700,
  typeFieldId: 'customfield_10042',
  typeBugDefaultValue: {
    id: '10105'
  },
  typeCommentDefaultValue: {
    id: '10106'
  },
  typeSuggestionDefaultValue: {
    id: '10107'
  },
  typeQuestionDefaultValue: {
    id: '10108'
  },
  typeEmptyDefaultValue: {
    id: 'empty'
  },
  showTypeField: true,
  showDefaultTextFields: true,
  anonymousFeedback: false,
  onClose: () => {},
  onSubmit: () => {}
});